import React, { forwardRef, useImperativeHandle, useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useAtom } from 'jotai';
import { totalParticipantCountAtom } from '../atoms/participantAtoms';
import toastQueue, { TOAST_CATEGORIES, participantToast, errorToast, successToast, infoToast, warningToast } from '../utils/toastQueue';
import { Search } from 'lucide-react';
import '../App.css';
import risehand_parti from '../assets/risehand_parti.png';
import mic_on_parti from '../assets/mic_on_parti.png';
import video_on_parti from '../assets/video_on_parti.png';
import video_off from '../assets/video_off.png';
import video_off_disable from '../assets/video_off_disable.png';
import mic_disable from '../assets/mic_disable.png';
import remove from '../assets/remove.png';
import mic_parti from '../assets/mic_parti.png';
import crossIcon from '../new_assets/cross.svg';
import participantIcon from '../new_assets/participant.svg';
import magnifyIcon from '../new_assets/maginfy.svg';
import onVideoIcon from '../new_assets/video_on.svg';
import offVideoIcon from '../new_assets/video_off.svg';
import offMicIcon from '../new_assets/mic_off.svg';
import onMicIcon from '../new_assets/mic_on.svg';
import ellipsisIcon from '../new_assets/ellipsis.svg';
import clockIcon from '../new_assets/clock.svg';
import { listOfAllParticipant } from '../model/util';
import {
  hideLobbyPopup,
  syncLobby,
  bulkLobbyUpdate,
  allowAllResponse,
  bulkParticipantUpdate,
  bulkParticipantError
} from '../receiveMessage';
let from = '', isRecordingOngoingSatus = 'initial', isUnmannedUser = false;
let participantArrBackup = [], lobbyArrBackup = [], recordingArrBackup = []
// fullPartiListFromServer = [];
const DisplayParticipantComponent = forwardRef(({ clientObjectArr, acsRoomId, userId, socket, isCallCreator, CbFromParticipant, params, resendRecordingPermission, resendRecordingPermissionForDenied, isDarkTheme, handlePinParticipant, handleUnpinParticipant }, ref) => {
  const [totalParticipantCount] = useAtom(totalParticipantCountAtom);
  const [isVisible, setIsVisible] = useState(false);
  const [displayParticipantArr, setDisplayParticipantArr] = useState([]);
  const [notJoinedParticipantArr, setNotJoinedParticipantArr] = useState([]);
  const [dynamicMessage, setDynamicMessage] = useState('');
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [openList, setOpenList] = useState(null);
  const [timers, setTimers] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const intervalRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [syncNotification, setSyncNotification] = useState('');
  const [isAllowAllInProgress, setIsAllowAllInProgress] = useState(false);

  // Enhanced search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const [localPinnedParticipantId, setLocalPinnedParticipantId] = useState(null);
  const [isPinning, setIsPinning] = useState(false); // Prevent rapid pin clicks
  const pinTimeoutRef = useRef(null);

  // Virtualized list state for performance
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 50 });
  const ITEM_HEIGHT = 60; // Approximate height of each participant item
  const CONTAINER_HEIGHT = 400; // Height of the visible container
  const BUFFER_SIZE = 10; // Extra items to render outside visible range

  const deniedRecordingParticipants = useMemo(() => {
    if (from !== 'participantForRecording') return [];
    return displayParticipantArr.filter(user => user.recordingStatus === 'reject');
  }, [displayParticipantArr, from]);

  const toggleList = (listName) => {
    setOpenList(openList === listName ? null : listName);
    selectedRadioCB(listName)
  };

  const handleLocalPinParticipant = (user) => {
    // Prevent rapid pin clicks
    if (isPinning) {
      console.log('Pin action already in progress, ignoring rapid click');
      return;
    }

    // Prevent double pinning the same user
    if (localPinnedParticipantId === user.userId) {
      console.log('User already pinned:', user.userId);
      infoToast.info(`${user.displayName} is already pinned`);
      return;
    }

    // Check if participant's camera is ON before pinning
    if (user.isVideoOff || !user.stream) {
      warningToast.warning("Cannot pin participant without camera.");
      return;
    }

    // Set pinning flag to prevent rapid clicks
    setIsPinning(true);

    // Clear any existing timeout
    if (pinTimeoutRef.current) {
      clearTimeout(pinTimeoutRef.current);
    }

    // If another participant was pinned, unpin them first
    let previousPinnedUser = null;
    if (localPinnedParticipantId) {
      previousPinnedUser = displayParticipantArr.find(p => p.userId === localPinnedParticipantId);
      console.log('Auto-unpinning previous user:', previousPinnedUser?.displayName);
    }

    // Update pin state
    setLocalPinnedParticipantId(user.userId);
    handlePinParticipant(user.userId, user.displayName, previousPinnedUser);

    // Show success toast
    successToast.success(`${user.displayName} pinned to screen.`);

    // Reset pinning flag after a short delay
    pinTimeoutRef.current = setTimeout(() => {
      setIsPinning(false);
    }, 500);

    setOpenDropdownId(null);
  }

  const handleLocalUnpinParticipant = (showToast = true) => {
    console.log("handleLocalUnpinParticipant");

    if (!localPinnedParticipantId) {
      console.log('No user is currently pinned');
      return;
    }

    // Find the unpinned user's name for toast
    const unpinnedUser = displayParticipantArr.find(p => p.userId === localPinnedParticipantId);
    const unpinnedUserName = unpinnedUser?.displayName || 'User';

    // Clear pin state
    setLocalPinnedParticipantId(null);
    handleUnpinParticipant(unpinnedUserName);

    // Show unpin toast only if requested (not when auto-unpinning)
    if (showToast) {
      infoToast.info(`${unpinnedUserName} unpinned from screen.`);
    }
    setOpenDropdownId(null);
  }

  // Suraj Karosia Changes Start

  // Function to calculate the elapsed time for a user
  const getElapsedTime = (userId, clientArr) => {
    const clientUser = clientArr?.find((c) => c.userId === userId);
    if (clientUser?.roomJoiningTime) {
      const currentTime = Math.floor(Date.now() / 1000);
      return Math.max(0, currentTime - clientUser.roomJoiningTime);
    }
    // Fallback to callDurationtTime if available
    const participant = displayParticipantArr.find(p => p.userId === userId);
    return participant?.callDurationtTime || 0;
  };

  // Synchronize timers when users change
  useEffect(() => {
    setTimers((prevTimers) => {
      const newTimers = {};
      // Only keep timers for current participants
      displayParticipantArr.forEach((user) => {
        // Preserve existing timers if available, otherwise initialize
        newTimers[user.userId] = prevTimers[user.userId] !== undefined ?
          prevTimers[user.userId] :
          getElapsedTime(user.userId, clientObjectArr);
        // getElapsedTime(user.userId, displayParticipantArr);
      });
      return newTimers;
    });
  }, [displayParticipantArr, clientObjectArr]);
  // }, [displayParticipantArr]);

  // Start a global interval to update timers every second
  useEffect(() => {
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setTimers((prevTimers) => {
          const updatedTimers = { ...prevTimers };
          Object.keys(updatedTimers).forEach((userId) => {
            updatedTimers[userId] += 1;
          });
          return updatedTimers;
        });
      }, 1000);
    }

    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, []);

  // Keep backup arrays in sync with clientObjectArr prop
  useEffect(() => {
    if (clientObjectArr && clientObjectArr.length > 0) {
      // Update backup arrays based on current clientObjectArr state
      const acceptedUsers = clientObjectArr.filter(user => user.isAcceptedByAdmin === true);
      const lobbyUsers = clientObjectArr.filter(user => user.isAcceptedByAdmin === false);
      const recordingUsers = clientObjectArr.filter(user => user.recordingStatus && user.recordingStatus !== 'init');

      // Always update backup arrays to ensure they're in sync
      participantArrBackup = [...acceptedUsers];
      lobbyArrBackup = [...lobbyUsers];
      recordingArrBackup = [...recordingUsers];

      console.log('🔄 Updated backup arrays:', {
        participants: participantArrBackup.length,
        lobby: lobbyArrBackup.length,
        recording: recordingArrBackup.length
      });

      // Check if pinned participant still exists and has valid state
      if (localPinnedParticipantId) {
        const pinnedUser = acceptedUsers.find(user => user.userId === localPinnedParticipantId);
        if (!pinnedUser) {
          // Pinned user no longer exists in participants
          console.log('Pinned participant no longer exists, auto-unpinning');
          handleLocalUnpinParticipant(false); // Don't show toast for automatic unpin
        }
        // Note: We don't unpin if camera turns off - user remains pinned as per requirement
      }
    }
  }, [clientObjectArr, localPinnedParticipantId]);

  // Update display when backup arrays change and corresponding tab is active
  useEffect(() => {
    if (from === 'participant' && participantArrBackup.length >= 0) {
      // Update participant display
      let tempArr = [...participantArrBackup];
      if (tempArr && tempArr.length > 1) {
        let index = tempArr.findIndex(item => item.userId === userId)
        if (index > -1) {
          let tempObj = tempArr[index];
          tempArr.splice(index, 1)
          tempArr.push(tempObj)
        }
      }
      setDisplayParticipantArr([...tempArr]);
    } else if (from === 'lobby' && lobbyArrBackup.length >= 0) {
      // Update lobby display
      setDisplayParticipantArr([...lobbyArrBackup]);
    } else if (from === 'participantForRecording' && recordingArrBackup.length >= 0) {
      // Update recording display
      setDisplayParticipantArr([...recordingArrBackup]);
    }
  }, [participantArrBackup, lobbyArrBackup, recordingArrBackup, from, userId]);

  // Cleanup toast queues and timeouts on unmount
  useEffect(() => {
    return () => {
      toastQueue.clearAll();
      // Clear pin timeout if exists
      if (pinTimeoutRef.current) {
        clearTimeout(pinTimeoutRef.current);
      }
    };
  }, []);


  // Function to format time as HH:MM:SS
  const formatTime = (userId) => {
    let seconds = timers[userId];

    if (seconds === undefined || isNaN(seconds)) {
      return "00:00:00";
    }
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = seconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  // Suraj Karosia Changes End

  function getInitials(str) {
    try {
      let initials = ''
      if (!str.includes(' ')) {
        initials = str.slice(0, 2).toUpperCase()
      } else {
        const words = str.split(' ');
        initials = words.map(word => word.charAt(0).toUpperCase()).join('');
        if (initials.length > 2) {
          initials = initials.slice(0, 2)
        }
      }
      return initials;
    } catch (error) {
      return str
    }
  }
  useImperativeHandle(ref, () => ({
    open: (fromAppJs = 'participant') => {
      setOpenList('inMeeting');
      from = fromAppJs;
      CbFromParticipant({ isOpen: true, from: fromAppJs })
      // console.log('-----params--', params)
      setIsVisible(true);
      setDynamicMessage('')
      if (from === 'participant') {
        setActiveTabIndex(0)
      } else if (from === 'participantForRecording') {
        setActiveTabIndex(2)
      } else {
        setActiveTabIndex(1)
      }
    },
    update: (partiArr) => {
      setOpenList('inMeeting');
      console.log('-----partiArr-update----', partiArr)
      if (from === 'participant') {
        participantArrBackup = [...partiArr];
        if (partiArr && partiArr.length > 1) {
          let index = partiArr.findIndex(item => item.userId === userId)
          if (index > -1) {
            let tempObj = partiArr[index];
            partiArr.splice(index, 1)
            partiArr.push(tempObj)
          }
        }
        setDisplayParticipantArr([...partiArr]);

      } else if (from === 'participantForRecording') {
        recordingArrBackup = [...partiArr];
        setDisplayParticipantArr([...partiArr]);
      } else {
        lobbyArrBackup = [...partiArr];
        setDisplayParticipantArr([...partiArr]);
      }

    },
    close: () => {
      CbFromParticipant({ isOpen: false, from: from })
      setIsVisible(false);
    },
    updateDynamicMsg: (msg) => {
      setDynamicMessage(msg)
    },
    updateRecordingFlag: (flag) => {
      isRecordingOngoingSatus = flag
    },
    updateUnnamedFlag: (flag) => {
      isUnmannedUser = flag
    }
  }));
  const clickOnParticipantTab = () => {
    //isSlectJoined = true;
    setOpenList('inMeeting');
    if (from !== 'participant') {
      //console.log('------tt------')
      CbFromParticipant({ isOpen: true, from: 'participant', reqFordata: 'participant' })
    } else {
      from = 'participant';
      setActiveTabIndex(0)
      setDisplayParticipantArr([...participantArrBackup]);
    }
    //console.log('----from parti-------', from);
  }
  const clickOnLobbyTab = () => {
    setOpenList('inMeeting');
    if (from !== 'lobby') {
      CbFromParticipant({ isOpen: true, from: 'lobby', reqFordata: 'lobby' })
    } else {
      from = 'lobby';
      setActiveTabIndex(1)
      setDisplayParticipantArr([...lobbyArrBackup]);
    }
  }
  const clickOnRecordingTab = () => {
    setOpenList('inMeeting');
    from = 'participantForRecording';
    setActiveTabIndex(2)
    setDisplayParticipantArr([...recordingArrBackup]);
    CbFromParticipant({ isOpen: true, from: 'participantForRecording', reqFordata: '' })
  }
  const selectedRadioCB = async (type) => {
    if (type === 'inMeeting') {
      setDisplayParticipantArr([...participantArrBackup]);
    } else {
      let fullPartiListFromServer = await listOfAllParticipant(params.tokenFromAzureLogin, params.progressId);

      if (fullPartiListFromServer && fullPartiListFromServer.length > 0) {
        const notJoinedArr = fullPartiListFromServer.filter(fullPart =>
          !participantArrBackup.some(joinedPart => joinedPart.uinfo === fullPart.uinfo)
        );
        if (notJoinedArr && notJoinedArr.length > 0)
          for (let i = 0; i < notJoinedArr.length; i++) {
            let name = notJoinedArr[i].uinfo.split('|')[3]
            notJoinedArr[i].displayName = name
          }
        setNotJoinedParticipantArr([...notJoinedArr]);
      } else {
        setNotJoinedParticipantArr([]);
      }

    }
  };

  // Suraj Karosia Changes Start for making co-host
  const toggleDropdown = (id) => {
    setOpenDropdownId(openDropdownId === id ? null : id);
  };

  const handleMakeCoHost = (user, index) => {
    if (isCallCreator && user.userType !== "Admin") {
      const isTwoAdmin = displayParticipantArr.filter(item => item.userType === "Admin");

      // Check if there are already 2 or more admins
      if (isTwoAdmin.length >= 2) {
        errorToast.error("A meeting can have only 1 admin and 1 co-host at a time");
        setOpenDropdownId(null);
        return;
      }

      // Check if user is already a co-host
      if (user.cohost) {
        infoToast.info(`${user.displayName} is already a co-host`);
        setOpenDropdownId(null);
        return;
      }

      // Update local state
      let tempArr = [...displayParticipantArr];
      tempArr[index].userType = "Admin";
      tempArr[index].cohost = true;
      tempArr[index].isCallCreator = true;

      const tempObj = {
        userId: user.userId,
        userType: "Admin",
        socketId: user.socketId,
        displayName: user.displayName,
        isRiseHand: user.isRiseHand,
        cohost: true,
        isCallCreator: true
      };

      // Emit the socket event
      socket.emit('BE-update-status', {
        acsRoomId: acsRoomId,
        senderUserId: user.userId,
        socketId: user.socketId,
        data: tempObj,
        type: 'makeCoHost',
        typeValue: true
      });

      setDisplayParticipantArr(tempArr);
      setOpenDropdownId(null);
      participantToast.success(`${user.displayName} is now a co-host`);
    }
  };

  const handleRemoveCoHost = (user, index) => {
    if (isCallCreator && user.userType === "Admin" && user.cohost) {
      // Update local state
      let tempArr = [...displayParticipantArr];
      tempArr[index].userType = "Other";
      tempArr[index].cohost = false;
      tempArr[index].isCallCreator = false;

      const tempObj = {
        userId: user.userId,
        userType: "Other",
        socketId: user.socketId,
        displayName: user.displayName,
        isRiseHand: user.isRiseHand,
        cohost: false,
        isCallCreator: false
      };

      // Emit the socket event
      socket.emit('BE-update-status', {
        acsRoomId: acsRoomId,
        senderUserId: user.userId,
        socketId: user.socketId,
        data: tempObj,
        type: 'removeCoHost',
        typeValue: false
      });

      setDisplayParticipantArr(tempArr);
      setOpenDropdownId(null);
      participantToast.success(`${user.displayName} is no longer a co-host`);
    }
  };

  const handleAskToStartVideo = (user, index) => {
    if (isCallCreator && user.userType !== "Admin") {
      const adminId = displayParticipantArr.find(
        (item) => userId === item?.userId && item?.userType === "Admin"
      )?.userId;
      console.log("AdminId in handleAskToUnmute function ===> ", adminId);
      const tempObj = {
        userId: user.userId,
        socketId: user.socketId,
        displayName: user.displayName,
        isRiseHand: user.isRiseHand,
        isCallCreator: true
      };

      // Emit the socket event
      socket.emit('BE-update-status', {
        acsRoomId: acsRoomId,
        senderUserId: user.userId,
        socketId: user.socketId,
        data: tempObj,
        type: 'AskToStartVideo',
        adminId: adminId
      });
      setOpenDropdownId(null);
    }
  };

  const handleAskToUnmute = (user, index) => {
    if (isCallCreator && user.userType !== "Admin") {
      const adminId = displayParticipantArr.find(
        (item) => userId === item?.userId && item?.userType === "Admin"
      )?.userId;
      const tempObj = {
        userId: user.userId,
        socketId: user.socketId,
        displayName: user.displayName,
        isRiseHand: user.isRiseHand,
        isCallCreator: true
      };

      // Emit the socket event
      socket.emit('BE-update-status', {
        acsRoomId: acsRoomId,
        senderUserId: user.userId,
        socketId: user.socketId,
        data: tempObj,
        type: 'AskToUnmute',
        adminId: adminId
      });
      setOpenDropdownId(null);
    }
  };

  // Bulk action functions for lobby
  const handleAllowAll = useCallback(() => {
    if (!isCallCreator || from !== 'lobby' || displayParticipantArr.length === 0 || isAllowAllInProgress) {
      return;
    }

    setIsAllowAllInProgress(true);

    // Get current lobby users (snapshot to handle edge cases)
    const currentLobbyUsers = displayParticipantArr.filter(user => !user.isAcceptedByAdmin);
    const userIds = currentLobbyUsers.map(user => user.userId);

    if (userIds.length === 0) {
      setIsAllowAllInProgress(false);
      warningToast.warning("No users in lobby to admit");
      return;
    }

    // Emit bulk allow event
    socket.emit('BE-bulk-allow-lobby', {
      acsRoomId: acsRoomId,
      userIds: userIds,
      adminUserId: userId
    });

    // Set timeout for error handling
    const timeoutId = setTimeout(() => {
      if (isAllowAllInProgress) {
        console.error('❌ Allow All operation timed out');
        setIsAllowAllInProgress(false);
        errorToast.error('Allow All operation timed out. Please try again.');
      }
    }, 10000); // 10 second timeout

    // Store timeout ID for cleanup
    return () => clearTimeout(timeoutId);
  }, [isCallCreator, from, displayParticipantArr, isAllowAllInProgress, acsRoomId, userId, socket]);

  const handleDenyAll = useCallback(() => {
    if (!isCallCreator || from !== 'lobby' || displayParticipantArr.length === 0 || isAllowAllInProgress) {
      return;
    }

    setIsAllowAllInProgress(true);

    try {
      // Get all user IDs from the lobby
      const userIds = displayParticipantArr.map(user => user.userId);

      if (userIds.length === 0) {
        warningToast.warning("No users in lobby to deny");
        setIsAllowAllInProgress(false);
        return;
      }

      // Emit bulk deny event
      socket.emit('BE-bulk-deny-lobby', {
        acsRoomId: acsRoomId,
        userIds: userIds,
        adminUserId: userId
      });

      // Clear the lobby arrays
      setDisplayParticipantArr([]);
      lobbyArrBackup = [];

      // Keep the lobby open even when empty - don't close the component
      console.log("🏁 Lobby cleared, but keeping it open for admin access");

      // Show success message
      successToast.success(`All ${userIds.length} users have been denied access`);
    } catch (error) {
      console.error('Error in handleDenyAll:', error);
      errorToast.error("Failed to deny users. Please try again.");
    } finally {
      setIsAllowAllInProgress(false);
    }
  }, [isCallCreator, from, displayParticipantArr, isAllowAllInProgress, acsRoomId, userId, socket]);

  // Bulk action functions for participants
  const [isParticipantActionInProgress, setIsParticipantActionInProgress] = useState(false);
  const [isAllRaiseDownInProgress, setIsAllRaiseDownInProgress] = useState(false);
  const [lastAllRaiseDownClick, setLastAllRaiseDownClick] = useState(0);

  const handleForceMuteAll = () => {
    if (!isCallCreator || from !== 'participant' || displayParticipantArr.length === 0 || isParticipantActionInProgress) {
      return;
    }

    try {
      setIsParticipantActionInProgress(true);

      // Get all participant IDs except admin/host and co-hosts
      const participantIds = displayParticipantArr
        .filter(user => user.userId !== userId && !user.isMute)
        .map(user => user.userId);

      if (participantIds.length === 0) {
        infoToast.info("No participants to mute");
        setIsParticipantActionInProgress(false);
        return;
      }

      // Check socket connection before emitting
      if (!socket || !socket.connected) {
        errorToast.error("Connection lost. Please check your network and try again.");
        setIsParticipantActionInProgress(false);
        return;
      }

      // Emit bulk force mute event
      socket.emit('BE-bulk-force-mute', {
        acsRoomId: acsRoomId,
        userIds: participantIds,
        adminUserId: userId
      });

      // Fallback timeout to ensure loading state is always reset
      setTimeout(() => {
        setIsParticipantActionInProgress(false);
      }, 10000); // 10 seconds timeout for safety
    } catch (error) {
      console.error('Error in handleForceMuteAll:', error);
      errorToast.error("Failed to mute participants. Please try again.");
      setIsParticipantActionInProgress(false);
    }
  };

  const handleForceVideoOffAll = () => {
    if (!isCallCreator || from !== 'participant' || displayParticipantArr.length === 0 || isParticipantActionInProgress) {
      return;
    }

    try {
      setIsParticipantActionInProgress(true);

      // Get all participant IDs except admin/host and co-hosts
      const participantIds = displayParticipantArr
        .filter(user => user.userId !== userId && !user.isVideoOff)
        .map(user => user.userId);

      if (participantIds.length === 0) {
        infoToast.info("No participants to turn off video for");
        setIsParticipantActionInProgress(false);
        return;
      }

      // Check socket connection before emitting
      if (!socket || !socket.connected) {
        errorToast.error("Connection lost. Please check your network and try again.");
        setIsParticipantActionInProgress(false);
        return;
      }

      // Emit bulk force video off event
      socket.emit('BE-bulk-force-video-off', {
        acsRoomId: acsRoomId,
        userIds: participantIds,
        adminUserId: userId
      });

      // Fallback timeout to ensure loading state is always reset
      setTimeout(() => {
        setIsParticipantActionInProgress(false);
      }, 10000); // 10 seconds timeout for safety
    } catch (error) {
      console.error('Error in handleForceVideoOffAll:', error);
      errorToast.error("Failed to turn off video for participants. Please try again.");
      setIsParticipantActionInProgress(false);
    }
  };

  const handleLowerAllHands = () => {
    if (!isCallCreator || from !== 'participant' || displayParticipantArr.length === 0 || isParticipantActionInProgress) {
      return;
    }

    try {
      setIsParticipantActionInProgress(true);

      // Get all participant IDs who have raised hands
      const participantIds = displayParticipantArr
        .filter(user => user.isRiseHand)
        .map(user => user.userId);

      if (participantIds.length === 0) {
        infoToast.info("No participants have raised hands");
        setIsParticipantActionInProgress(false); // Fix: Reset state on early return
        return;
      }

      // Emit bulk lower all hands event
      socket.emit('BE-bulk-lower-hands', {
        acsRoomId: acsRoomId,
        userIds: participantIds,
        adminUserId: userId
      });

      // Fallback timeout to ensure loading state is always reset
      setTimeout(() => {
        setIsParticipantActionInProgress(false);
      }, 10000); // 10 seconds timeout for safety
    } catch (error) {
      console.error('Error in handleLowerAllHands:', error);
      errorToast.error("Failed to lower hands for participants. Please try again.");
      setIsParticipantActionInProgress(false);
    }
  };

  // Check if any hands are raised
  const hasRaisedHands = useMemo(() => {
    return displayParticipantArr.some(user => user.isRiseHand);
  }, [displayParticipantArr]);

  // Check if all participants (excluding admin/co-hosts) are already muted
  // Note: Always checks ALL participants regardless of search query for consistency
  const areAllParticipantsMuted = useMemo(() => {
    const regularParticipants = displayParticipantArr.filter(
      user => user.userId !== userId && user.userType !== "Admin" && !user.cohost
    );

    if (regularParticipants.length === 0) return true;

    return regularParticipants.every(user => user.isMute);
  }, [displayParticipantArr, userId]);

  // Check if all participants (excluding admin/co-hosts) already have video off
  // Note: Always checks ALL participants regardless of search query for consistency
  const areAllParticipantsVideoOff = useMemo(() => {
    const regularParticipants = displayParticipantArr.filter(
      user => user.userId !== userId && user.userType !== "Admin" && !user.cohost
    );

    if (regularParticipants.length === 0) return true;

    return regularParticipants.every(user => user.isVideoOff);
  }, [displayParticipantArr, userId]);

  // Handle "All Raise Down" button with debounce protection
  const handleAllRaiseDown = useCallback(() => {
    const now = Date.now();
    const DEBOUNCE_TIME = 2000; // 2 seconds debounce

    // Check debounce
    if (now - lastAllRaiseDownClick < DEBOUNCE_TIME) {
      warningToast.warning("Please wait before clicking again");
      return;
    }

    if (!isCallCreator || from !== 'participant' || !hasRaisedHands || isAllRaiseDownInProgress) {
      return;
    }

    try {
      setIsAllRaiseDownInProgress(true);
      setLastAllRaiseDownClick(now);

      // Get all participant IDs who have raised hands
      const participantIds = displayParticipantArr
        .filter(user => user.isRiseHand)
        .map(user => user.userId);

      if (participantIds.length === 0) {
        infoToast.info("No participants have raised hands");
        return;
      }

      // Emit bulk lower all hands event
      socket.emit('BE-bulk-lower-hands', {
        acsRoomId: acsRoomId,
        userIds: participantIds,
        adminUserId: userId
      });

      // Show success message
      participantToast.success(`Lowered hands for all ${participantIds.length} participants`);
    } catch (error) {
      console.error('Error in handleAllRaiseDown:', error);
      errorToast.error("Failed to lower all hands. Please try again.");
    } finally {
      // Reset loading state after a delay
      setTimeout(() => {
        setIsAllRaiseDownInProgress(false);
      }, 2000);
    }
  }, [isCallCreator, from, hasRaisedHands, isAllRaiseDownInProgress, lastAllRaiseDownClick, displayParticipantArr, acsRoomId, userId, socket]);

  // Handle edge cases: user disconnections and network issues
  useEffect(() => {
    // Reset button state when participants change (handles user disconnections)
    if (!hasRaisedHands && isAllRaiseDownInProgress) {
      setIsAllRaiseDownInProgress(false);
    }
  }, [hasRaisedHands, isAllRaiseDownInProgress]);

  // Handle network reconnection scenarios
  useEffect(() => {
    const handleReconnect = () => {
      // Reset button state on reconnection
      setIsAllRaiseDownInProgress(false);
      setLastAllRaiseDownClick(0);
    };

    // Listen for socket reconnection events
    socket.on('connect', handleReconnect);
    socket.on('reconnect', handleReconnect);

    return () => {
      socket.off('connect', handleReconnect);
      socket.off('reconnect', handleReconnect);
    };
  }, [socket]);

  // Handle dynamic positioning of context menus
  useEffect(() => {
    if (openDropdownId) {
      // Find the button that triggered the dropdown using data attribute
      const targetButton = document.querySelector(`button[data-dropdown-id="${openDropdownId}"][title="More options"]`);

      if (targetButton) {
        const rect = targetButton.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Find the corresponding menu
        const menu = targetButton.parentElement.querySelector('.context-menu');
        if (menu) {
          const menuRect = menu.getBoundingClientRect();
          const menuHeight = menuRect.height || 200; // Use actual height if available, otherwise estimate
          const menuWidth = menuRect.width || 160;

          const spaceBelow = viewportHeight - rect.bottom;
          const spaceAbove = rect.top;
          const spaceRight = viewportWidth - rect.right;
          const spaceLeft = rect.left;

          // Determine if menu should open above or below
          let shouldOpenAbove = false;
          if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
            shouldOpenAbove = true;
          } else if (spaceBelow < menuHeight && spaceAbove < menuHeight) {
            // If neither direction has enough space, prefer below
            shouldOpenAbove = false;
          }

          // Determine if menu should open left or right
          let shouldOpenLeft = false;
          if (spaceRight < menuWidth && spaceLeft > menuWidth) {
            shouldOpenLeft = true;
          } else if (spaceRight < menuWidth && spaceLeft < menuWidth) {
            // If neither direction has enough space, prefer right
            shouldOpenLeft = false;
          }

          if (shouldOpenAbove) {
            // Open above
            menu.style.top = 'auto';
            menu.style.bottom = '70%';
            menu.style.marginTop = '0';
            menu.style.marginBottom = '8px';
          } else {
            // Open below
            menu.style.top = '70%';
            menu.style.bottom = 'auto';
            menu.style.marginTop = '8px';
            menu.style.marginBottom = '0';
          }

          if (shouldOpenLeft) {
            // Open to the left
            menu.style.right = 'auto';
            menu.style.left = '-150px';
            menu.style.marginRight = '0';
            menu.style.marginLeft = '8px';
          } else {
            // Open to the right (default)
            menu.style.right = '0';
            menu.style.left = 'auto';
            menu.style.marginRight = '8px';
            menu.style.marginLeft = '0';
          }
        }
      }
    }
  }, [openDropdownId]);

  useEffect(() => {
    if (!openDropdownId) return;

    const handleOutsideClick = (event) => {
      const target = event.target;
      const isMenuClick = target.closest('.context-menu');
      const toggleButton = target.closest('button[data-dropdown-id]');

      if (isMenuClick) return;
      if (toggleButton && toggleButton.getAttribute('data-dropdown-id') === String(openDropdownId)) {
        return;
      }

      setOpenDropdownId(null);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [openDropdownId]);

  // const renderUserControls = useCallback((user, currentUser, index) => {
  //   const isViewerAdmin = currentUser?.userType === "Admin" || isCallCreator;
  //   const isTargetRegularUser = user?.userType !== "Admin";
  //   const isTargetCoHost = user?.userType === "Admin" && user?.cohost;

  //   const getMenuItemStyle = (key) => ({
  //     padding: "10px 16px",
  //     cursor: "pointer",
  //     fontSize: "14px",
  //     transition: "background 0.2s",
  //     display: "flex",
  //     alignItems: "center",
  //     gap: "8px",
  //     color: key === "remove" || key === "removeCohost" ? "red" : "#333",
  //     fontWeight: key === "remove" || key === "removeCohost" ? 500 : 400,
  //     backgroundColor: hoveredItem === key ? "#f0f0f0" : "#fff",
  //     // whiteSpace: "nowrap",
  //     userSelect: "none",
  //   });


  //   // Show three-dot menu for regular users (non-admin) OR for co-hosts
  //   if (isViewerAdmin && (isTargetRegularUser || isTargetCoHost)) {
  //     return (
  //       <div style={{ position: "relative", display: "inline-block" }}>
  //         <button
  //           style={{
  //             background: "transparent",
  //             border: "none",
  //             fontSize: "24px",
  //             cursor: "pointer",
  //             padding: "0px 10px",
  //             color: "#555",
  //           }}
  //           onClick={() => toggleDropdown(user.userId)}
  //           title="More options"
  //           data-dropdown-id={user.userId}
  //         >
  //           &#8942;
  //         </button>

  //         {openDropdownId === user.userId && (
  //           <div
  //             className="context-menu"
  //             style={{
  //               position: "absolute",
  //               backgroundColor: "#fff",
  //               border: "1px solid #ddd",
  //               borderRadius: "8px",
  //               boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
  //               width: "160px",
  //               zIndex: 1000,
  //               animation: "fadeIn 0.2s ease-in-out",
  //             }}
  //           >
  //             <ul style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>
  //               {/* Show "Make Co-Host" only for regular users */}
  //               {isTargetRegularUser && (
  //                 <li
  //                   style={getMenuItemStyle("cohost")}
  //                   onMouseEnter={() => setHoveredItem("cohost")}
  //                   onMouseLeave={() => setHoveredItem(null)}
  //                   onClick={() => handleMakeCoHost(user, index)}
  //                 >
  //                   Make Co-Host
  //                 </li>
  //               )}

  //               {/* Show "Remove Co-Host" only for co-hosts */}
  //               {isTargetCoHost && (
  //                 <li
  //                   style={getMenuItemStyle("removeCohost")}
  //                   onMouseEnter={() => setHoveredItem("removeCohost")}
  //                   onMouseLeave={() => setHoveredItem(null)}
  //                   onClick={() => handleRemoveCoHost(user, index)}
  //                 >
  //                   Remove Co-Host
  //                 </li>
  //               )}


  //               <li
  //                 style={getMenuItemStyle("chat")}
  //                 onClick={() => {
  //                   socket.emit('BE-update-status', { acsRoomId: acsRoomId, senderUserId: user.userId, socketId: user.socketId, data: null, type: 'ForceRemoved', typeValue: true });
  //                 }}
  //               >
  //                 Remove
  //               </li>

  //               {localPinnedParticipantId === user?.userId ? (
  //                 <li
  //                   onClick={handleLocalUnpinParticipant}
  //                   style={getMenuItemStyle("cohost")}
  //                 >
  //                   Unpin Participant
  //                 </li>
  //               ) : (
  //                 <li
  //                   onClick={() => handleLocalPinParticipant(user)}
  //                   style={getMenuItemStyle("cohost")}
  //                 >
  //                   Pin Participant
  //                 </li>
  //               )}

  //               {user?.isMute && (
  //                 <li
  //                   style={getMenuItemStyle("mic")}
  //                   onMouseEnter={() => setHoveredItem("mic")}
  //                   onMouseLeave={() => setHoveredItem(null)}
  //                   onClick={() => handleAskToUnmute(user, index)}
  //                 >
  //                   Ask to Unmute
  //                 </li>
  //               )}
  //               {user.isVideoOff && (
  //                 <li
  //                   style={getMenuItemStyle("video")}
  //                   onMouseEnter={() => setHoveredItem("video")}
  //                   onMouseLeave={() => setHoveredItem(null)}
  //                   onClick={() => handleAskToStartVideo(user, index)}
  //                 >
  //                   Ask to Turn On Camera
  //                 </li>
  //               )}
  //               {/* <li
  //                 style={getMenuItemStyle("remove")}
  //                 onMouseEnter={() => setHoveredItem("remove")}
  //                 onMouseLeave={() => setHoveredItem(null)}
  //               >
  //                  Remove
  //               </li> */}
  //             </ul>
  //           </div>
  //         )}
  //       </div>
  //     );
  //   }

  //   return null;
  // }, [openDropdownId, toggleDropdown, handleMakeCoHost, handleRemoveCoHost, isCallCreator]);


  const currentUserObj = useMemo(() => {
    return (
      displayParticipantArr.find((u) => u.userId === userId) || {
        userId,
        userType: isCallCreator ? "Admin" : "Other",
      }
    );
  }, [displayParticipantArr, isCallCreator, userId]);

  const renderUserControls = useCallback(
    (user, currentUser, index) => {
      if (user?.userId === currentUser?.userId) return null;

      const isViewerHost = isCallCreator;
      const isViewerAdmin = currentUser?.userType === "Admin";
      const isViewerCoHost = isViewerAdmin && currentUser?.cohost;
      const isViewerOther = currentUser?.userType === "Other";

      const canManageUsers = isViewerHost || isViewerCoHost;

      const isTargetAdmin = user?.userType === "Admin";
      const isTargetCoHost = isTargetAdmin && user?.cohost;
      const isTargetRegularUser = user?.userType === "Other";

      const canShowThreeDot =
        canManageUsers || isViewerOther;

      if (!canShowThreeDot) return null;

      const getMenuItemStyle = (key) => ({
        padding: "10px 16px",
        cursor: "pointer",
        fontSize: "14px",
        transition: "background 0.2s",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        color:
          key === "remove" || key === "removeCohost" ? "red" : "#333",
        fontWeight:
          key === "remove" || key === "removeCohost" ? 500 : 400,
        backgroundColor:
          hoveredItem === key ? "#f0f0f0" : "#fff",
        userSelect: "none",
      });

      return (
        <div style={{ position: "relative", display: "inline-block" }}>
          {/* Three-dot button */}
          <button
            style={{
              background: "transparent",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              padding: "0 8px",
              color: "#555",
            }}
            onClick={() => toggleDropdown(user.userId)}
            title="More options"
          >
            &#8942;
          </button>

          {/* Dropdown */}
          {openDropdownId === user.userId && (
            <div
              className="context-menu"
              style={{
                position: "absolute",
                right: 0,
                backgroundColor: "#fff",
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                width: "180px",
                zIndex: 1000,
              }}
            >
              <ul style={{ listStyle: "none", margin: 0, padding: "8px 0" }}>

                {/* =====================
                    ADMIN / CO-HOST ONLY
                ====================== */}
                {canManageUsers && (
                  <>
                    {isTargetRegularUser && (
                      <li
                        style={getMenuItemStyle("cohost")}
                        onClick={() => handleMakeCoHost(user, index)}
                      >
                        Make Co-Host
                      </li>
                    )}

                    {isTargetCoHost && (
                      <li
                        style={getMenuItemStyle("removeCohost")}
                        onClick={() => handleRemoveCoHost(user, index)}
                      >
                        Remove Co-Host
                      </li>
                    )}

                    <li
                      style={getMenuItemStyle("remove")}
                      onClick={() =>
                        socket.emit("BE-update-status", {
                          acsRoomId,
                          senderUserId: user.userId,
                          socketId: user.socketId,
                          type: "ForceRemoved",
                          typeValue: true,
                        })
                      }
                    >
                      Remove
                    </li>

                    {user?.isMute && (
                      <li
                        style={getMenuItemStyle("mic")}
                        onClick={() => handleAskToUnmute(user, index)}
                      >
                        Ask to Unmute
                      </li>
                    )}

                    {user?.isVideoOff && (
                      <li
                        style={getMenuItemStyle("video")}
                        onClick={() => handleAskToStartVideo(user, index)}
                      >
                        Ask to Turn On Camera
                      </li>
                    )}
                  </>
                )}

                {/* =====================
                    PIN / UNPIN (ALL USERS)
                ====================== */}
                {localPinnedParticipantId === user?.userId ? (
                  <li
                    style={getMenuItemStyle("pin")}
                    onClick={handleLocalUnpinParticipant}
                  >
                    Unpin Participant
                  </li>
                ) : (
                  <li
                    style={getMenuItemStyle("pin")}
                    onClick={() => handleLocalPinParticipant(user)}
                  >
                    Pin Participant
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      );
    },[openDropdownId,toggleDropdown,hoveredItem,isCallCreator,displayParticipantArr,userId,localPinnedParticipantId,handleMakeCoHost,handleRemoveCoHost,handleLocalPinParticipant,handleLocalUnpinParticipant,handleAskToUnmute,handleAskToStartVideo]);

  useEffect(() => {
    // Hide lobby popup handler
    hideLobbyPopup(socket, ({ userId }) => {
      // Don't auto-close lobby when empty - let admin keep it open
      if (displayParticipantArr.length === 0) {
        console.log("🏁 Lobby is empty, but keeping it open for admin access");
      }
    });

    // Handle lobby synchronization across all admins/co-hosts
    syncLobby(socket, ({ acsRoomId, userId, action, timestamp }) => {
      if (from === 'lobby' && displayParticipantArr.length > 0) {
        // Remove the user from the local lobby array
        const updatedArr = displayParticipantArr.filter(user => user.userId !== userId);
        setDisplayParticipantArr(updatedArr);

        // Update the backup array as well
        if (action === 'admitted' || action === 'denied' || action === 'left') {
          lobbyArrBackup = lobbyArrBackup.filter(user => user.userId !== userId);
        }

        console.log(`✅ Removed user ${userId} from lobby. Remaining users: ${updatedArr.length}`);

        // Show sync notification based on action
        let message = '';
        if (action === 'admitted') {
          message = `User was admitted by another admin`;
        } else if (action === 'denied') {
          message = `User was denied by another admin`;
        } else if (action === 'left') {
          message = `User left the waiting room`;
        }

        if (message) {
          setSyncNotification(message);
          setTimeout(() => setSyncNotification(''), 3000);
        }

        // Don't auto-close lobby when empty - let admin keep it open
        if (updatedArr.length === 0) {
          console.log("🏁 Lobby is now empty, but keeping it open for admin access");
        }
      }
    });

    // Handle bulk lobby operations
    bulkLobbyUpdate(socket, ({ acsRoomId, action, userIds, adminUserId }) => {
      if (from === 'lobby' && displayParticipantArr.length > 0) {
        // Remove all users from the local lobby array
        const updatedArr = displayParticipantArr.filter(user => !userIds.includes(user.userId));
        setDisplayParticipantArr(updatedArr);

        // Update the backup array as well
        lobbyArrBackup = lobbyArrBackup.filter(user => !userIds.includes(user.userId));

        console.log(`✅ Removed ${userIds.length} users from lobby. Remaining users: ${updatedArr.length}`);

        // Show sync notification
        setSyncNotification(`${userIds.length} users were ${action} by another admin`);
        setTimeout(() => setSyncNotification(''), 3000);

        // Don't auto-close lobby when empty - let admin keep it open
        if (updatedArr.length === 0) {
          console.log("🏁 Lobby is now empty, but keeping it open for admin access");
        }
      }
    });

    // Handle Allow All response
    allowAllResponse(socket, ({ success, message, userIds, adminUserId }) => {
      setIsAllowAllInProgress(false);

      if (success) {
        successToast.success(`Successfully admitted ${userIds.length} users to the meeting`);

        // Close the participant component after successful admission
        setTimeout(() => {
          CbFromParticipant({ isOpen: false, from: from });
          setIsVisible(false);
          setOpenDropdownId(null);
        }, 0); // Small delay to show the success message
      } else {
        errorToast.error(`Failed to admit users: ${message}`);
      }
    });

    // Handle bulk participant operations
    bulkParticipantUpdate(socket, ({ acsRoomId, action, userIds, adminUserId }) => {
      if (from === 'participant' && displayParticipantArr.length > 0) {
        // Reset loading state when receiving server confirmation
        if (adminUserId === userId && (action === 'forceMute' || action === 'forceVideoOff' || action === 'lowerHands')) {
          setIsParticipantActionInProgress(false);
        }

        // Update the local participant array
        const updatedArr = displayParticipantArr.map(user => {
          if (userIds.includes(user.userId)) {
            if (action === 'forceMute') {
              return { ...user, isMute: true };
            } else if (action === 'forceVideoOff') {
              return { ...user, isVideoOff: true };
            } else if (action === 'lowerHands') {
              return { ...user, isRiseHand: false };
            }
          }
          return user;
        });
        setDisplayParticipantArr(updatedArr);

        // Update the backup array as well
        if (from === 'participant') {
          participantArrBackup = participantArrBackup.map(user => {
            if (userIds.includes(user.userId)) {
              if (action === 'forceMute') {
                return { ...user, isMute: true };
              } else if (action === 'forceVideoOff') {
                return { ...user, isVideoOff: true };
              } else if (action === 'lowerHands') {
                return { ...user, isRiseHand: false };
              }
            }
            return user;
          });
        }

        console.log(`✅ Updated ${userIds.length} participants with ${action}`);

        // Show success notification when current admin performed the action
        if (adminUserId === userId) {
          if (action === 'forceMute') {
            participantToast.success(`Microphone muted for ${userIds.length} participant${userIds.length > 1 ? 's' : ''}`);
          } else if (action === 'forceVideoOff') {
            participantToast.success(`Camera turned off for ${userIds.length} participant${userIds.length > 1 ? 's' : ''}`);
          } else if (action === 'lowerHands') {
            participantToast.success(`Lowered hands for ${userIds.length} participant${userIds.length > 1 ? 's' : ''}`);
          }
        } else {
          // Show sync notification when another admin performed the action
          setSyncNotification(`${userIds.length} participant${userIds.length > 1 ? 's were' : ' was'} ${action === 'forceMute' ? 'muted' : action === 'forceVideoOff' ? 'video turned off' : 'hands lowered'} by another admin`);
          setTimeout(() => setSyncNotification(''), 3000);
        }
      }
    });

    // Handle bulk participant operation errors
    bulkParticipantError(socket, ({ acsRoomId, action, error, failedUserIds, adminUserId }) => {
      if (from === 'participant') {
        // Reset the action in progress state
        setIsParticipantActionInProgress(false);

        // Show error notification
        errorToast.error(`Failed to ${action}: ${error}`);

        // If there were partial failures, show additional info
        if (failedUserIds && failedUserIds.length > 0) {
          console.warn(`Failed user IDs:`, failedUserIds);
        }
      }
    });

    return () => {
      socket.off('FE-hide-lobby-popup');
      socket.off('FE-sync-lobby');
      socket.off('FE-bulk-lobby-update');
      socket.off('FE-allow-all-response');
      socket.off('FE-bulk-participant-update');
      socket.off('FE-bulk-participant-error');
    };
  }, [displayParticipantArr, from]);

  // Monitor lobby array but don't auto-close when empty - let admin keep it open
  useEffect(() => {
    if (from === 'lobby' && displayParticipantArr.length > 0 && isVisible) {
      console.log(`🔄 Lobby has ${displayParticipantArr.length} users waiting`);
    } else if (from === 'lobby' && displayParticipantArr.length === 0 && isVisible) {
      console.log("🏁 Lobby is empty, but keeping it open for admin access");
    }
  }, [displayParticipantArr, from, isVisible]);

  // Enhanced debounced search effect with 2-second delay
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const trimmedQuery = searchQuery.trim();

    if (trimmedQuery) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
      setDebouncedSearchQuery('');
      setSearchResults([]);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchQuery(trimmedQuery);
      setIsSearching(false);
    }, 1000); // 2-second debounce delay for better performance

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Enhanced filtered participants with performance optimization
  const filteredParticipants = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return displayParticipantArr;
    }

    const query = debouncedSearchQuery.toLowerCase().trim();
    const results = displayParticipantArr.filter(user => {
      const displayName = user.displayName?.toLowerCase() || '';
      // Support for non-English characters and partial matching
      return displayName.includes(query) ||
        displayName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(query);
    });

    setSearchResults(results);
    return results;
  }, [displayParticipantArr, debouncedSearchQuery]);

  // Enhanced search input handler with performance optimization
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Clear search results immediately if input is empty
    if (!value.trim()) {
      setSearchResults([]);
      setDebouncedSearchQuery('');
    }
  }, []);

  // Enhanced clear search handler
  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setDebouncedSearchQuery('');
    setIsSearching(false);

    // Focus back to input for better UX
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  // Enhanced search with keyboard shortcuts
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      handleClearSearch();
    }
  }, [handleClearSearch]);


  // Clear search when switching tabs or closing component
  useEffect(() => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
  }, [activeTabIndex, isVisible]);

  // Real-time search updates when participants change
  useEffect(() => {
    if (debouncedSearchQuery.trim() && displayParticipantArr.length > 0) {
      // Re-apply search when participants are added/removed
      const query = debouncedSearchQuery.toLowerCase().trim();
      const results = displayParticipantArr.filter(user => {
        const displayName = user.displayName?.toLowerCase() || '';
        return displayName.includes(query) ||
          displayName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(query);
      });
      setSearchResults(results);
    }
  }, [displayParticipantArr, debouncedSearchQuery]);

  // Virtualized list scroll handler
  const handleScroll = useCallback((e) => {
    const scrollTop = e.target.scrollTop;
    const start = Math.floor(scrollTop / ITEM_HEIGHT);
    const end = Math.min(
      start + Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT) + BUFFER_SIZE,
      filteredParticipants.length
    );

    setVisibleRange({
      start: Math.max(0, start - BUFFER_SIZE),
      end
    });
  }, [filteredParticipants.length]);

  // Reset visible range when participants change
  useEffect(() => {
    setVisibleRange({ start: 0, end: Math.min(50, filteredParticipants.length) });
  }, [filteredParticipants.length]);

  // Memoized visible participants for virtualization
  const visibleParticipants = useMemo(() => {
    return filteredParticipants.slice(visibleRange.start, visibleRange.end);
  }, [filteredParticipants, visibleRange]);

  // Calculate total height for virtual scrolling
  const totalHeight = filteredParticipants.length * ITEM_HEIGHT;


  if (!isVisible) return null;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          .context-menu {
            transition: all 0.2s ease-in-out;
          }
        `}
      </style>
      <div className='participant-panel'>
        <div className='panelHeader'>
          <h6 className='title'>
            <img src={participantIcon} className='icon' />
            {`Participant (${totalParticipantCount})`}
          </h6>
          <span className='close' onClick={() => {
            // Allow closing in all cases - admin can manually close lobby even when empty
            CbFromParticipant({ isOpen: false, from: from })
            setIsVisible(false)
            setOpenDropdownId(null);
          }}>
            <img src={crossIcon} />
          </span>
        </div>
        <div className='panelTabs'>
          <div className={`tab ${from == 'participant' && 'active'}`}
            onClick={() => clickOnParticipantTab()}
            style={!isCallCreator ? { width: '100%' } : {}}>
            {`In Call (${participantArrBackup.length})`}
          </div>
          {isCallCreator && (
            <div className={`tab ${from == 'lobby' && 'active'}`}
              onClick={() => clickOnLobbyTab()}>
              {`Waiting (${lobbyArrBackup.length})`}
            </div>
          )}
          {isCallCreator && (
            <div className={`tab ${from == 'participantForRecording' && 'active'}`}
              onClick={() => clickOnRecordingTab()}>
              {`Recording (${recordingArrBackup.length})`}
            </div>
          )}
        </div>
        <div className='userSection'>
          {/* <div className='searchUser'>
              <input className='input' 
              value={searchQuery}
              onChange={handleSearchChange}
              type='text' 
              onFocus={(e) => e.target.style.borderColor = '#0167FF'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
              placeholder='Enter name to search...' />
              <img src={magnifyIcon} onClick={handleClearSearch} className='inputIcon' />
            </div> */}

          {/* Enhanced search bar for participants - only show when in participant mode */}
          {from === 'participant' && (
            <div style={{ marginBottom: '15px' }}>
              <div className='searchUser' style={{ position: 'relative' }}>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search participants by name..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  className='input'
                  style={{
                    paddingRight: searchQuery ? '40px' : '35px',
                    fontSize: '11px',
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#0167FF'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  aria-label="Search participants by name"
                  aria-describedby="search-help"
                  autoComplete="off"
                  spellCheck="false"
                />

                {/* Search/Clear icon */}
                {searchQuery ? (
                  <button
                    onClick={handleClearSearch}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontSize: '16px'
                    }}
                    aria-label="Clear search"
                    title="Clear search (Esc)"
                  >
                    ✕
                  </button>
                ) : (
                  <img
                    src={magnifyIcon}
                    className='inputIcon'
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      opacity: 0.6
                    }}
                    alt="Search"
                  />
                )}
              </div>

              {/* Search status and results */}
              {searchQuery && (
                <div style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: '#666',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexWrap: 'wrap'
                }}>
                  {isSearching ? (
                    <>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        border: '2px solid #0167FF',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                      <span>Searching...</span>
                    </>
                  ) : (
                    <>
                      {filteredParticipants.length === 0 ? (
                        <span style={{ color: '#FF6B6B', fontWeight: '500' }}>
                          No participants found for "{searchQuery}"
                        </span>
                      ) : (
                        <span>
                          Found <strong>{filteredParticipants.length}</strong> of <strong>{displayParticipantArr.length}</strong> participants
                        </span>
                      )}
                      {searchQuery && (
                        <button
                          onClick={handleClearSearch}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0167FF',
                            cursor: 'pointer',
                            fontSize: '12px',
                            textDecoration: 'underline',
                            padding: '0'
                          }}
                        >
                          Clear search
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Accessibility help text */}
              <div
                id="search-help"
                style={{
                  fontSize: '11px',
                  color: '#999',
                  marginTop: '4px',
                  display: 'none' // Hidden but available for screen readers
                }}
              >
                Search participants by name. Use Escape key to clear search.
              </div>
            </div>
          )}

          {openList === 'inMeeting' &&
            (
              <div className='userList'>
                {/* Allow All button for lobby */}
                {from === 'lobby' && isCallCreator && displayParticipantArr.length > 1 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginBottom: "20px",
                    }}
                  >
                    {/* Left Side Text */}
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#A2AAC7",
                      }}
                    >
                      {displayParticipantArr.length}{" "}
                      {displayParticipantArr.length > 1 ? "peoples are waiting to join" : "person is waiting to join"}
                    </span>

                    {/* Right Side Button */}
                    <button
                      onClick={handleAllowAll}
                      disabled={isAllowAllInProgress || displayParticipantArr.length <= 1}
                      className='controlBtn admit-all'
                      style={{
                        backgroundColor: isAllowAllInProgress ? "#EDF2F7" : undefined,
                        opacity: isAllowAllInProgress || displayParticipantArr.length <= 1 ? 0.6 : 1,
                        width: "100%",
                      }}
                    >
                      {isAllowAllInProgress ? (
                        <>
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              border: "2px solid #fff",
                              borderTop: "2px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                              marginRight: "8px",
                            }}
                          />
                          Processing...
                        </>
                      ) : (
                        `Admit All (${displayParticipantArr.length})`
                      )}
                    </button>
                  </div>

                )}

                {/* Force Mute All and Force Video Off All buttons for participants */}
                {from === 'participant' && isCallCreator && displayParticipantArr.length > 2 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginBottom: "15px",
                    }}
                  >
                    {/* Left Side Text */}
                    <span
                      style={{
                        fontSize: "13px",
                        fontWeight: "400",
                      }}
                      className='controlTitle'
                    >
                      Admin Controls
                    </span>

                    {/* Right Side Buttons */}
                    <div
                      style={{
                        display: "flex",
                        gap: "6px",
                        flexWrap: "nowrap",
                        width: "100%",
                        maxWidth: "300px",
                      }}
                    >
                      {/* Force Mute All Button */}
                      <button
                        onClick={handleForceMuteAll}
                        disabled={isParticipantActionInProgress || displayParticipantArr.length <= 1 || areAllParticipantsMuted}
                        className='controlBtn mute-all'
                        style={{
                          backgroundColor: isParticipantActionInProgress ? "#EDF2F7" : undefined,
                          opacity: isParticipantActionInProgress || displayParticipantArr.length <= 1 || areAllParticipantsMuted ? 0.6 : 1,
                        }}
                        title={areAllParticipantsMuted ? "All participants are already muted" : "Mute all participants"}
                      >
                        {isParticipantActionInProgress ? (
                          <>
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                border: "2px solid #fff",
                                borderTop: "2px solid transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            />
                            Processing...
                          </>
                        ) : (
                          <>
                            <img
                              src={offMicIcon}
                              style={{ width: "14px", height: "14px" }}
                              alt="Mute All"
                            />
                            Mute All
                          </>
                        )}
                      </button>

                      {/* Force Video Off All Button */}
                      <button
                        onClick={handleForceVideoOffAll}
                        disabled={isParticipantActionInProgress || displayParticipantArr.length <= 2 || areAllParticipantsVideoOff}
                        className='controlBtn video-off-all'
                        style={{
                          backgroundColor: isParticipantActionInProgress ? "#EDF2F7" : undefined,
                          opacity: isParticipantActionInProgress || displayParticipantArr.length <= 2 || areAllParticipantsVideoOff ? 0.6 : 1,
                        }}
                        title={areAllParticipantsVideoOff ? "All participants already have video turned off" : "Turn off video for all participants"}
                      >
                        {isParticipantActionInProgress ? (
                          <>
                            <div
                              style={{
                                width: "10px",
                                height: "10px",
                                border: "2px solid #fff",
                                borderTop: "2px solid transparent",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                              }}
                            />
                            Processing...
                          </>
                        ) : (
                          <>
                            <img
                              src={offVideoIcon}
                              style={{ width: "14px", height: "14px" }}
                              alt="Video Off All"
                            />
                            Video Off All
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {from === 'participantForRecording' && isCallCreator && deniedRecordingParticipants.length > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '10px',
                      marginBottom: '16px',
                      backgroundColor: '#F8FAFC',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  >
                    <span
                      style={{
                        fontSize: '13px',
                        color: '#1E293B',
                        fontWeight: 500,
                        flex: 1,
                        minWidth: '180px'
                      }}
                    >
                      {deniedRecordingParticipants.length} {deniedRecordingParticipants.length === 1 ? 'participant has' : 'participants have'} denied recording consent.
                    </span>

                    <button
                      onClick={() => {
                        if (typeof resendRecordingPermissionForDenied === 'function') {
                          resendRecordingPermissionForDenied();
                        }
                      }}
                      className='controlBtn'
                      style={{
                        backgroundColor: '#2563EB',
                        color: '#fff',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)'
                      }}
                    >
                      Resend All
                    </button>
                  </div>
                )}

                {/* No participants message */}
                {(from === 'participant' ? filteredParticipants : displayParticipantArr) && (from === 'participant' ? filteredParticipants : displayParticipantArr).length > 0 ? null : (
                  <div style={{
                    fontSize: 14,
                    fontStyle: 'italic',
                    color: '#282D38',
                    marginTop: 10,
                    textAlign: 'center',
                    padding: '20px'
                  }}
                    className='noUser'>
                    {from === 'lobby' ? 'No participants waiting in lobby' :
                      searchQuery ? `No participants found for "${searchQuery}"` :
                        'No request found'}
                  </div>
                )}

                {/* Virtualized participant list for performance */}
                {from === 'participant' && filteredParticipants.length > 0 ? (
                  <div
                    // style={{
                    //   height: `${Math.min(CONTAINER_HEIGHT, totalHeight)}px`,
                    //   overflowY: 'auto',
                    //   overflowX: 'hidden'
                    // }}
                    style={{ height: "100%", overflowY: 'auto', overflowX: 'hidden' }}
                    onScroll={handleScroll}
                  >
                    {visibleParticipants.map((user, index) => {
                      const actualIndex = visibleRange.start + index;
                      return (
                        <div key={user.userId || actualIndex} className='item' style={{ height: `${ITEM_HEIGHT}px` }}>
                          <div className='icon'>{getInitials(user.displayName)}</div>
                          <div className='info' style={{ display: 'flex', flexDirection: 'column' }}>
                            {/* <span className='name'>{user.userId === userId ? 'You' : user.displayName}</span> */}
                            <span className='name'>
                              {user.userId === userId
                                ? 'You'
                                : user.displayName
                              }
                              {user.userType === "Admin" && !user.cohost && (
                                <span className="label"> (Host)</span>
                              )}
                              {user.userType === "Admin" && user.cohost && (
                                <span className="label"> (Co-Host)</span>
                              )}
                            </span>
                            <span className='timer'>{formatTime(user?.userId)}</span>
                            {/* <div className='configRow'>
                              <div className='config'>
                                <span className={`status ${!user.isMute ? 'active' : ''}`}></span>
                                Mic {user.isMute ? 'off' : 'on'}
                              </div>
                              <div className='config'>
                                <span className={`status ${!user.isVideoOff ? 'active' : ''}`}></span>
                                Camera {user.isVideoOff ? 'off' : 'on'}
                              </div>
                            </div> */}
                          </div>
                          {/* <ul className='actions'>
                    <li className='action'>
                      <img className='btn' src={onVideoIcon}></img>
                    </li>
                    <li className='action'>
                      <img className='btn' src={offMicIcon}></img>
                    </li>
                    <li className='action'>
                      <img className='btn' src={ellipsisIcon}></img>
                    </li>
                  </ul> */}

                          {from === 'participant' ? (
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                              {/* <span className='timer'>{formatTime(user?.userId)}</span> */}
                              {isCallCreator ? (
                                <ul className='actions'>
                                  <li className='action'>
                                    <img
                                      src={user.isVideoOff ? offVideoIcon : onVideoIcon}
                                      className='btn'
                                      onClick={() => {
                                        let tempArr = [...displayParticipantArr];
                                        if (isCallCreator && !tempArr[index].isVideoOff) {
                                          tempArr[index].isVideoOff = true;
                                          const tempObj = {
                                            userId: user.userId,
                                            userType: isCallCreator ? 'Admin' : 'Other',
                                            socketId: user.socketId,
                                            displayName: user.displayName,
                                            isRiseHand: user.isRiseHand,
                                          };
                                          socket.emit('BE-update-status', {
                                            acsRoomId: acsRoomId,
                                            senderUserId: user.userId,
                                            socketId: user.socketId,
                                            data: tempObj,
                                            type: 'forceVideoOff',
                                            typeValue: true,
                                          });
                                          setDisplayParticipantArr(tempArr);
                                        } else {
                                          console.log('Condition not met - forceVideoOff skipped');
                                        }
                                      }}
                                      style={{
                                        height: 18,
                                        width: 18,
                                        resizeMode: 'contain',
                                        cursor: isCallCreator ? 'pointer' : 'default',
                                        marginLeft: 7,
                                      }}
                                      data-title={user.isVideoOff ? 'Video is already off' : 'Force Video Off'}
                                    />
                                  </li>

                                  <li className='action'>
                                    <img
                                      className='btn'
                                      src={user.isMute ? offMicIcon : onMicIcon}
                                      onClick={() => {
                                        let tempArr = [...displayParticipantArr];
                                        if (isCallCreator && !tempArr[index].isMute) {
                                          tempArr[index].isMute = true;
                                          const tempObj = {
                                            userId: user.userId,
                                            userType: isCallCreator ? 'Admin' : 'Other',
                                            socketId: user.socketId,
                                            displayName: user.displayName,
                                            isRiseHand: user.isRiseHand,
                                          };

                                          socket.emit('BE-update-status', {
                                            acsRoomId: acsRoomId,
                                            senderUserId: user.userId,
                                            socketId: user.socketId,
                                            data: tempObj,
                                            type: 'forceMute',
                                            typeValue: true,
                                          });

                                          setDisplayParticipantArr(tempArr);
                                        } else {
                                          console.log('🚫 Cannot mute: Either not call creator or already muted');
                                        }
                                      }}
                                      style={{
                                        height: 18,
                                        width: 18,
                                        resizeMode: 'contain',
                                        cursor: isCallCreator ? 'pointer' : 'default',
                                        marginLeft: 7,
                                      }}
                                      data-title={user.isMute ? 'Microphone is already muted' : 'Force Mute'}
                                    />
                                  </li>

                                  {/* {user.userId == userId ? (
                                <div style={{ marginLeft: 0 }} />
                              ) : (
                                <li className='action'>
                                  <img
                                    src={remove}
                                    className='btn'
                                    onClick={() => {
                                      socket.emit('BE-update-status', {
                                        acsRoomId: acsRoomId,
                                        senderUserId: user.userId,
                                        socketId: user.socketId,
                                        data: null,
                                        type: 'ForceRemoved',
                                        typeValue: true,
                                      });
                                    }}
                                    style={{
                                      height: 20,
                                      width: 20,
                                      resizeMode: 'contain',
                                      cursor: 'pointer',
                                      marginLeft: 7,
                                    }}
                                    data-title="Remove Participant"
                                  />
                                </li>
                              )} */}

                                  {(user?.userType === 'Other' || (user?.userType === 'Admin' && user?.cohost)) &&
                                    renderUserControls(user, currentUserObj, index)}
                                </ul>
                              ) : (
                                <ul className='actions'>
                                  <li className='action'>
                                    <img
                                      src={user.isVideoOff ? video_off : video_on_parti}
                                      className='btn'
                                    />
                                  </li>
                                  <li className='action'>
                                    <img
                                      className='btn'
                                      src={user.isMute ? mic_parti : mic_on_parti}
                                      style={{
                                        height: 20,
                                        width: 20,
                                        resizeMode: 'contain',
                                        cursor: isCallCreator ? 'pointer' : 'default',
                                        marginLeft: 7,
                                      }}
                                    />
                                  </li>
                                  <li className='action'>
                                    {renderUserControls(user, currentUserObj, index)}
                                  </li>
                                </ul>
                              )}
                            </div>
                          ) : from === 'lobby' ? (
                            <div
                              className='btnGrp'
                            >
                              <div
                                className="btn admit"
                                onClick={() => {
                                  user.recordingOnGoingStatus = isRecordingOngoingSatus;
                                  socket.emit('BE-hide-lobby-popup', { acsRoomId: acsRoomId, userId: user.userId });
                                  socket.emit('BE-admit-room', {
                                    acsRoomId: acsRoomId,
                                    userId: user.userId,
                                    SocketId: user.socketId,
                                    dataObj: user,
                                    value: '',
                                    type: 'AcceptLobby',
                                  });

                                  let tempArr = [...displayParticipantArr];
                                  tempArr.splice(index, 1);
                                  setDisplayParticipantArr(tempArr);

                                  lobbyArrBackup = lobbyArrBackup.filter((u) => u.userId !== user.userId);

                                  socket.emit('BE-sync-lobby', {
                                    acsRoomId: acsRoomId,
                                    userId: user.userId,
                                    action: 'admitted',
                                    timestamp: Math.floor(Date.now() / 1000),
                                  });

                                  if (tempArr.length === 0) {
                                    setIsVisible(false);
                                    CbFromParticipant({ isOpen: false, from: 'lobby' });
                                  }
                                }}
                              >
                                Admit
                              </div>

                              <div
                                className="btn"
                                onClick={() => {
                                  socket.emit('BE-hide-lobby-popup', { acsRoomId: acsRoomId, userId: user.userId });

                                  let tempArr = [...displayParticipantArr];
                                  tempArr.splice(index, 1);
                                  setDisplayParticipantArr(tempArr);

                                  lobbyArrBackup = lobbyArrBackup.filter((u) => u.userId !== user.userId);

                                  socket.emit('BE-sync-lobby', {
                                    acsRoomId: acsRoomId,
                                    userId: user.userId,
                                    action: 'denied',
                                    timestamp: Math.floor(Date.now() / 1000),
                                  });

                                  if (tempArr.length === 0) {
                                    setIsVisible(false);
                                    CbFromParticipant({ isOpen: false, from: 'lobby' });
                                  }

                                  socket.emit('BE-update-status', {
                                    acsRoomId: acsRoomId,
                                    senderUserId: user.userId,
                                    socketId: user.socketId,
                                    data: null,
                                    type: 'RejectLobby',
                                    typeValue: true,
                                  });
                                }}
                              >
                                Deny
                              </div>
                            </div>
                          ) : (
                            <div
                              className='btnGrp'
                              style={{
                                display: 'flex',
                                flexDirection: user.recordingStatus === 'reject' ? 'column' : 'row',
                                gap: '4px',
                                alignItems: 'center'
                              }}
                            >
                              <div
                                style={{
                                  fontSize: '9px',
                                  background: user.recordingStatus === 'init'
                                    ? '#fff'
                                    : user.recordingStatus === 'accept'
                                      ? '#16A249'
                                      : '#FEF2F2',
                                  border: user.recordingStatus === 'init'
                                    ? '1px solid #ECEFF3'
                                    : user.recordingStatus === 'accept'
                                      ? 'none'
                                      : '1px solid #FECACA',
                                  color: user.recordingStatus === 'init'
                                    ? '#6B7280'
                                    : user.recordingStatus === 'accept'
                                      ? '#fff'
                                      : '#DC2626',
                                  padding: '4px 6px',
                                  borderRadius: '3px',
                                  cursor: 'default',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  minWidth: '55px',
                                  justifyContent: 'center',
                                  fontWeight: user.recordingStatus === 'reject' ? '600' : '500'
                                }}
                              >
                                {user.recordingStatus === 'init' && (
                                  <div style={{
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    backgroundColor: '#F59E0B',
                                    animation: 'pulse 2s infinite'
                                  }} />
                                )}
                                {user.recordingStatus === 'accept' && (
                                  <span style={{ fontSize: '12px' }}>✓</span>
                                )}
                                {user.recordingStatus === 'reject' && (
                                  <span style={{ fontSize: '12px' }}>✕</span>
                                )}
                                {user.recordingStatus === 'init'
                                  ? 'Pending'
                                  : user.recordingStatus === 'accept'
                                    ? 'Accepted'
                                    : 'Denied'}
                              </div>

                              {user.recordingStatus === 'reject' && (
                                <div
                                  onClick={() => {
                                    resendRecordingPermission(user.userId);
                                  }}
                                  style={{
                                    fontSize: '9px',
                                    background: '#EF4444',
                                    border: 'none',
                                    color: '#fff',
                                    padding: '4px 6px',
                                    borderRadius: '3px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    fontWeight: '600',
                                    transition: 'all 0.2s ease',
                                    boxShadow: '0 1px 2px rgba(239, 68, 68, 0.3)'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = '#DC2626';
                                    e.target.style.transform = 'translateY(-1px)';
                                    e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.4)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = '#EF4444';
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 1px 2px rgba(239, 68, 68, 0.3)';
                                  }}
                                >
                                  <span style={{ fontSize: '9px' }}>↻</span>
                                  Resend
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  /* Fallback for non-participant modes (lobby, recording) */
                  (from === 'lobby' ? displayParticipantArr : displayParticipantArr).map((user, index) => (
                    <div key={user.userId || index} className='item'>
                      <div className='icon'>{getInitials(user.displayName)}</div>
                      <div className='info'>
                        <span className='name'>{user.userId === userId ? 'You' : user.displayName}</span>
                        {/* <div className='configRow'>
                          <div className='config'>
                            <span className={`status ${!user.isMute ? 'active' : ''}`}></span>
                            Mic {user.isMute ? 'off' : 'on'}
                          </div>
                          <div className='config'>
                            <span className={`status ${!user.isVideoOff ? 'active' : ''}`}></span>
                            Camera {user.isVideoOff ? 'off' : 'on'}
                          </div>
                        </div> */}
                      </div>

                      {from === 'lobby' ? (
                        <div className='btnGrp'>
                          <div
                            className="btn admit"
                            onClick={() => {
                              user.recordingOnGoingStatus = isRecordingOngoingSatus;
                              socket.emit('BE-hide-lobby-popup', { acsRoomId: acsRoomId, userId: user.userId });
                              socket.emit('BE-admit-room', {
                                acsRoomId: acsRoomId,
                                userId: user.userId,
                                SocketId: user.socketId,
                                dataObj: user,
                                value: '',
                                type: 'AcceptLobby',
                              });

                              let tempArr = [...displayParticipantArr];
                              tempArr.splice(index, 1);
                              setDisplayParticipantArr(tempArr);

                              lobbyArrBackup = lobbyArrBackup.filter((u) => u.userId !== user.userId);

                              socket.emit('BE-sync-lobby', {
                                acsRoomId: acsRoomId,
                                userId: user.userId,
                                action: 'admitted',
                                timestamp: Math.floor(Date.now() / 1000),
                              });

                              if (tempArr.length === 0) {
                                setIsVisible(false);
                                CbFromParticipant({ isOpen: false, from: 'lobby' });
                              }
                            }}
                          >
                            Admit
                          </div>

                          <div
                            className="btn"
                            onClick={() => {
                              socket.emit('BE-hide-lobby-popup', { acsRoomId: acsRoomId, userId: user.userId });

                              let tempArr = [...displayParticipantArr];
                              tempArr.splice(index, 1);
                              setDisplayParticipantArr(tempArr);

                              lobbyArrBackup = lobbyArrBackup.filter((u) => u.userId !== user.userId);

                              socket.emit('BE-sync-lobby', {
                                acsRoomId: acsRoomId,
                                userId: user.userId,
                                action: 'denied',
                                timestamp: Math.floor(Date.now() / 1000),
                              });

                              if (tempArr.length === 0) {
                                setIsVisible(false);
                                CbFromParticipant({ isOpen: false, from: 'lobby' });
                              }

                              socket.emit('BE-update-status', {
                                acsRoomId: acsRoomId,
                                senderUserId: user.userId,
                                socketId: user.socketId,
                                data: null,
                                type: 'RejectLobby',
                                typeValue: true,
                              });
                            }}
                          >
                            Deny
                          </div>
                        </div>
                      ) : (
                        <div
                          className='btnGrp'
                          style={{
                            display: 'flex',
                            flexDirection: user.recordingStatus === 'reject' ? 'column' : 'row',
                            gap: '4px',
                            alignItems: 'center'
                          }}
                        >
                          <div
                            style={{
                              fontSize: '9px',
                              background: user.recordingStatus === 'init'
                                ? '#fff'
                                : user.recordingStatus === 'accept'
                                  ? '#16A249'
                                  : '#FEF2F2',
                              border: user.recordingStatus === 'init'
                                ? '1px solid #ECEFF3'
                                : user.recordingStatus === 'accept'
                                  ? 'none'
                                  : '1px solid #FECACA',
                              color: user.recordingStatus === 'init'
                                ? '#6B7280'
                                : user.recordingStatus === 'accept'
                                  ? '#fff'
                                  : '#DC2626',
                              padding: '4px 6px',
                              borderRadius: '3px',
                              cursor: 'default',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '3px',
                              minWidth: '55px',
                              justifyContent: 'center',
                              fontWeight: user.recordingStatus === 'reject' ? '600' : '500'
                            }}
                          >
                            {user.recordingStatus === 'init' && (
                              <div style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                backgroundColor: '#F59E0B',
                                animation: 'pulse 2s infinite'
                              }} />
                            )}
                            {user.recordingStatus === 'accept' && (
                              <span style={{ fontSize: '12px' }}>✓</span>
                            )}
                            {user.recordingStatus === 'reject' && (
                              <span style={{ fontSize: '12px' }}>✕</span>
                            )}
                            {user.recordingStatus === 'init'
                              ? 'Waiting'
                              : user.recordingStatus === 'accept'
                                ? 'Accepted'
                                : 'Denied'}
                          </div>

                          {user.recordingStatus === 'reject' && (
                            <div
                              onClick={() => {
                                resendRecordingPermission(user.userId);
                              }}
                              style={{
                                fontSize: '9px',
                                background: '#EF4444',
                                border: 'none',
                                color: '#fff',
                                padding: '4px 6px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                fontWeight: '600',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 1px 2px rgba(239, 68, 68, 0.3)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.background = '#DC2626';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 2px 4px rgba(239, 68, 68, 0.4)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.background = '#EF4444';
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 1px 2px rgba(239, 68, 68, 0.3)';
                              }}
                            >
                              <span style={{ fontSize: '9px' }}>↻</span>
                              Resend
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}

                {/* All Raise Down Button - Only visible when hands are raised */}
                {from === 'participant' && isCallCreator && hasRaisedHands && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      marginBottom: "15px",
                    }}
                  >
                    <button
                      onClick={handleAllRaiseDown}
                      disabled={isAllRaiseDownInProgress}
                      className='controlBtn all-raise-down'
                      style={{
                        backgroundColor: "#FF6B35",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        padding: "12px 20px",
                        fontSize: "14px",
                        fontWeight: "600",
                        cursor: isAllRaiseDownInProgress ? "not-allowed" : "pointer",
                        opacity: isAllRaiseDownInProgress ? 0.7 : 1,
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        transition: "all 0.2s ease",
                        position: "absolute",
                        bottom: "10px",
                        width: "20%",
                        zIndex: "9999",
                      }}
                      onMouseEnter={(e) => {
                        if (!isAllRaiseDownInProgress) {
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
                      }}
                    >
                      {isAllRaiseDownInProgress ? (
                        <>
                          <div
                            style={{
                              width: "16px",
                              height: "16px",
                              border: "2px solid #fff",
                              borderTop: "2px solid transparent",
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          />
                          Processing...
                        </>
                      ) : (
                        <>
                          <img
                            src={risehand_parti}
                            style={{ width: "16px", height: "16px", filter: "invert(1)" }}
                            alt="All Raise Down"
                          />
                          All Raise Down
                        </>
                      )}
                    </button>
                  </div>
                )}

              </div>
            )}

        </div>
      </div>
    </>
  );
});

export default DisplayParticipantComponent;